import { faCircleEllipsis } from "@fortawesome/pro-regular-svg-icons/faCircleEllipsis";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider, IconButton, Menu, MenuItem } from "@mui/material";
import { MprcConfirmDialog, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { FeedType, PublicationAttachType, PublicationType } from "generated/CoreApi_v1";
import { TFunction } from "i18next";
import HtmlUtils from "lib/HtmlUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { SessionService } from "services/sessionService";
import { GeneratorQuestionAIPub } from "views/assessment/generatorQuestion/GeneratorQuestionAIPub";
import { CardContextType } from "views/knowledge/components/card";
import { BlockDialog } from "views/knowledge/components/card/actions/BlockDialog";
import { PublicationLogDialog } from "views/knowledge/components/card/actions/PublicationLogDialog";
import { PublicationRelationshipDialog } from "views/knowledge/components/card/actions/PublicationRelationshipDialog";
import { PublicationThumbnailDialog } from "views/knowledge/components/card/actions/PublicationThumbnailDialog";
import { ReportErrorOrAbuseDialog } from "views/knowledge/components/card/actions/ReportErrorOrAbuseDialog";
import { UnblockPublication } from "views/knowledge/components/card/actions/UnblockPublication";
import { UpdatePosterDialog } from "views/knowledge/components/card/actions/UpdatePosterDialog";
import { DeletePublicationFactory } from "views/knowledge/components/card/behaviors/common/DeletePublicationFactory";
import { EditPublicationFactory } from "views/knowledge/components/card/behaviors/common/EditPublicationFactory";
import { useCardContext } from "views/knowledge/components/card/CardBuilder";
import { publish } from "views/knowledge/feed/FeedEvents";
import { classifyFactory, publicationFactory } from "views/knowledge/feed/FeedHelpers";
import { PublicationInAnswer } from "views/knowledge/publication/creation";
import { EditShortVideoDialog } from "views/knowledge/shorts/Home/EditShorts/EditShortVideoDialog";
import { DoHidePublication } from "../../actions/DoHidePublication";
import { ModeratorMessageRejectDialog } from "../../actions/ModeratorMessageDialog";
import { ThreadPublicationsreOrderDialog } from "../../actions/ThreadPublicationsreOrderDialog";
import { VideoTranscriptionDrawer } from "../../actions/VideoTranscriptionDrawer";
import { WhoLikesDrawer } from "../../actions/WhoLikesDrawer";
import { WhoViewDrawer } from "../../actions/WhoViewDrawer";
import { TrashedOutOptions } from "./TrashedOutOptions";

type OptionMode = "default" | "question" | "answer";

type MenuOptions =
  | "edit"
  | "delete"
  | "classify"
  | "relatedContent"
  | "customThumbnail"
  | "channelPinned"
  | "createTask"
  | "createQuestion"
  | "generateAIQuestion"
  | "viewTranscription"
  | "promoteShorts"
  | "download"
  | "downloadDoc"
  | "whoLikes"
  | "whoView"
  | "history"
  | "hideForYou"
  | "abuseOrError"
  | "block"
  | "unblock"
  | "moderatorMessage"
  | "reorderThreads"
  | "copyLink"
  | "updatePoster"
  | "qrCode"
  ;

type MenuItemOption = { key: MenuOptions, label: string; checked?: boolean; disabled?: boolean; };

///
function buildGroupOption(context: CardContextType, t: TFunction, optionMode: OptionMode) {
  const groupOptions: Array<Array<MenuItemOption>> = [];

  const {
    pub: {
      masterPublication,
      channelPinnedOn,
      isDraft,
      readOnly,
      interaction: {
        allowDelete,
        allowBlock,
        allowUnblock,
        allowEdit,
        allowClassify,
        allowChannelPinned,
        allowDownloadVideoOrAudio,
        allowPromoteVideo,
        allowViewHistory,
        allowWhoView,
        allowWhoLikes,
        allowCreateQuestion,
        allowRelatedContent,
        allowReorder,
        allowGenerateAIQuestion,
        isVisible
      },
      rejected,
      firstApprovalDate,
      attachs
    },
    cardOwnProps: {
      presentation: {
        currentIsVideo,
        currentAttachId
      }
    },
    otherInfo
  } = context;

  ///

  const currentAttach = attachs && attachs.find(p => p.attachId == currentAttachId);
  const uniqueAttachVideo = attachs && attachs.length == 1 && attachs[0].attachType == PublicationAttachType.Video ? attachs[0] : null;
  const uniqueAttachIsAudio = attachs && attachs.length == 1 && attachs[0].attachType == PublicationAttachType.Audio;
  const uniqueAttachIsDoc = attachs && attachs.length == 1 && attachs[0].attachType == PublicationAttachType.Document;
  const uniqueAttachHasTranscription = attachs && attachs.length == 1 && attachs[0].hasTranscription;
  const uniqueAttachIsMedia = attachs && attachs.length == 1 &&
    (attachs[0].attachType == PublicationAttachType.Video
      || attachs[0].attachType == PublicationAttachType.YouTube
      || attachs[0].attachType == PublicationAttachType.Vimeo
      || attachs[0].attachType == PublicationAttachType.Audio
    );

  if (isDraft || firstApprovalDate == undefined) {

    const groupItems1: MenuItemOption[] = [];
    if (uniqueAttachIsMedia || (currentAttachId && currentIsVideo)) {

      if (allowEdit && (uniqueAttachVideo?.fileEncodedUrls != null || currentAttach?.fileEncodedUrls != null)) {
        groupItems1.push({ key: "updatePoster", label: t("Feed.Card.Options.UpdatePosterAction") });
      }

      if (uniqueAttachHasTranscription || currentAttach?.hasTranscription == true) {
        groupItems1.push({ key: "viewTranscription", label: t("Feed.Card.Options.ViewTranscriptionAction") });
      }

      if (uniqueAttachVideo || uniqueAttachIsAudio || (currentAttachId && currentIsVideo)) {
        if (allowDownloadVideoOrAudio && !isDraft) groupItems1.push({ key: "download", label: t("Feed.Card.Options.DownloadAction") });
      }

      if (groupItems1.length > 0) groupOptions.push(groupItems1);
    }

    const groupItems2: MenuItemOption[] = [];
    if (allowViewHistory) groupItems2.push({ key: "history", label: t("Feed.Card.Options.HistoryAction") });
    if (groupItems2.length > 0) groupOptions.push(groupItems2);

    if (rejected?.message != undefined) groupOptions.push([{ key: "moderatorMessage", label: t("Feed.Card.Options.ModeratorMessageAction") }]);

  } else {
    const groupItems1: MenuItemOption[] = [];

    if (allowEdit) groupItems1.push({ key: "edit", label: t("Feed.Card.Options.EditAction") });
    if (allowClassify) groupItems1.push({ key: "classify", label: t("Feed.Card.Options.ClassifyAction") });
    if (allowRelatedContent) groupItems1.push({ key: "relatedContent", label: t("Feed.Card.Options.RelatedContentAction") });

    if (otherInfo?.feedType == FeedType.ContentPage && allowReorder) groupItems1.push({ key: "reorderThreads", label: t("Feed.Card.Options.ReorderAction") });
    if (allowEdit && masterPublication == null && optionMode == "default") groupItems1.push({ key: "customThumbnail", label: t("Feed.Card.Options.CustomThumbnailAction") });

    if (allowGenerateAIQuestion) groupItems1.push({ key: "generateAIQuestion", label: t("Feed.Card.Options.GenerateAIQuestion"), disabled: readOnly });

    if (otherInfo?.feedType == FeedType.Channel && allowChannelPinned) groupItems1.push({ key: "channelPinned", label: t("Feed.Card.Options.ChannelPinnedAction"), checked: channelPinnedOn != null });
    if (groupItems1.length > 0) groupOptions.push(groupItems1);

    const groupItems2: MenuItemOption[] = [];
    if (allowCreateQuestion) groupItems2.push({ key: "createQuestion", label: t("Feed.Card.Options.CreateQuestionAction"), disabled: readOnly });
    groupItems2.push({ key: "copyLink", label: t("Feed.Card.Options.CopyLink"), disabled: readOnly });
    groupItems2.push({ key: "qrCode", label: t("Feed.Card.Options.QrCode") });
    if (groupItems2.length > 0) groupOptions.push(groupItems2);

    const groupItems4: MenuItemOption[] = [];
    if (uniqueAttachIsMedia || (currentAttachId && currentIsVideo)) {

      if (allowEdit && (uniqueAttachVideo?.fileEncodedUrls != null || currentAttach?.fileEncodedUrls != null)) {
        groupItems4.push({ key: "updatePoster", label: t("Feed.Card.Options.UpdatePosterAction") });
      }

      if (uniqueAttachHasTranscription || currentAttach?.hasTranscription == true) {
        groupItems4.push({ key: "viewTranscription", label: t("Feed.Card.Options.ViewTranscriptionAction") });
      }

      if (uniqueAttachVideo || uniqueAttachIsAudio || (currentAttachId && currentIsVideo)) {
        if (allowDownloadVideoOrAudio) groupItems4.push({ key: "download", label: t("Feed.Card.Options.DownloadAction") });
      }
    }
    else if (allowEdit && uniqueAttachIsDoc) {
      groupItems4.push({ key: "downloadDoc", label: t("Feed.Card.Options.DownloadAction") });
    }

    if (allowPromoteVideo) groupItems4.push({ key: "promoteShorts", label: t("Feed.Card.Options.PromoteShortsAction"), disabled: readOnly });

    if (groupItems4.length > 0) groupOptions.push(groupItems4);

    const groupItems5: MenuItemOption[] = [];
    if (allowWhoLikes) groupItems5.push({ key: "whoLikes", label: t("Feed.Card.Options.WhoLikesAction") });
    if (allowWhoView) groupItems5.push({ key: "whoView", label: t("Feed.Card.Options.WhoViewAction") });
    if (allowViewHistory) groupItems5.push({ key: "history", label: t("Feed.Card.Options.HistoryAction") });
    if (groupItems5.length > 0) groupOptions.push(groupItems5);

    const groupItems6: MenuItemOption[] = [];
    if ((otherInfo?.feedType == FeedType.Startup
      || otherInfo?.feedType == FeedType.Channel
      || otherInfo?.feedType == FeedType.Topic))
      groupItems6.push({ key: "hideForYou", label: t("Feed.Card.Options.HideForYouAction"), disabled: readOnly, checked: !isVisible });

    groupItems6.push({ key: "abuseOrError", label: t("Feed.Card.Options.AbuseOrReportAction"), disabled: readOnly });

    if (allowBlock) {
      groupItems6.push({ key: "block", label: t("Feed.Card.Options.BlockAction"), disabled: readOnly });
    }
    else if (allowUnblock && otherInfo?.feedType == FeedType.PublicationAdmin) {
      groupItems6.push({ key: "unblock", label: t("Feed.Card.Options.UnblockAction") });
    }

    if (allowDelete) groupItems6.push({ key: "delete", label: t("Feed.Card.Options.RemoveAction") });
    if (groupItems6.length > 0) groupOptions.push(groupItems6);
  }

  return groupOptions;
}

///
const BaseDefaultOptions: React.FC<{ optionMode: OptionMode; }> = (props) => {

  const { optionMode } = props;

  const { t } = useTranslation("knowledge");
  const { enqueueSnackbar } = useSnackbar();
  const api = useCoreApi();
  const cardContext = useCardContext();

  const [anchor, setAnchor] = useState<Element | null>(null);
  const [dialog, setDialog] = useState<any>(null);
  const [posting, setPosting] = useState<boolean>(false);
  const [viewError, setViewError] = useState<any>(null);

  const {
    pub: {
      publicationId,
      publicationType,
      masterPublication,
      listExclusive,
      title,
      isDraft,
      deletedOn,
      interaction,
      answerDefinition,
      attachs
    },
    cardOwnProps: {
      presentation: {
        currentAttachId
      }
    },
    onFeedItemChanged,
    onFeedReload,
    onReload
  } = useCardContext();

  const pubId = optionMode == "question" && answerDefinition ? answerDefinition.questionId : publicationId;
  const pubType = optionMode == "question" ? PublicationType.Question : publicationType;
  const groupOptions = buildGroupOption(cardContext, t, optionMode);

  ///
  const closeDialog = () => {
    setDialog(null);
  };

  ///
  const handleCloseClassify = (changed: boolean) => {
    if (changed) {
      onReload();
    }
    setDialog(null);
  };


  ///
  const copyLink = () => {
    const host = window.location.protocol + "//" + window.location.host;
    const publicationUrl = `${host}${KnowledgePaths.PublicationViewerResolver(publicationId, publicationType, masterPublication?.publicationId)}`;

    HtmlUtils.copyToClipboard(publicationUrl)
      .then(() => enqueueSnackbar(t("Feed.Card.Common.CopyLinkSuccessMessage"), { variant: "success" }))
      .catch(() => enqueueSnackbar(t("Feed.Card.Common.CopyLinkFailMessage"), { variant: "error" }));
  };

  ///
  const qrcode = () => {
    HtmlUtils.openUrlInNewWindow(
      KnowledgePaths.QrCode(publicationId)
    );
  };

  // Opções do menu
  const menuItems = useMemo(() => {
    var arr: React.ReactNode[] = [];

    groupOptions.forEach((g, gindex) => {
      g.forEach((v) => {
        arr.push(
          <MenuItem
            key={v.key}
            disabled={v.disabled}
            sx={(theme) => ({ color: v.key == "delete" ? theme.palette.error.main : undefined })}
            onClick={
              () => {
                handleChooseMenuItem(v.key);
                setAnchor(null);
              }
            }
          >
            {
              v.checked
                ?
                <React.Fragment>
                  <b>
                    <FontAwesomeIcon fixedWidth icon={faCheck} />
                  </b>
                </React.Fragment>
                :
                <div className="fa fa-check fa-w-16 fa-fw"></div>
            }
            {v.label}
          </MenuItem>);
      });

      if (gindex < groupOptions.length - 1)
        arr.push(<Divider key={gindex} />);
    });

    return arr;
  }, [groupOptions]);

  ///
  const handleClosePublication = () => {
    setDialog(null);
  };

  ///
  const handleChannelPinned = () => {
    setPosting(true);
    api.v1.knowledgeFeed.publicationToogleChannelPinned({ publicationId })
      .then(() => onFeedReload && onFeedReload())
      .catch((error) => setViewError(error))
      .finally(() => setPosting(false));
  };

  ///
  const handleChooseMenuItem = (option: MenuOptions) => {
    const currentAttach = attachs && attachs.find(p => p.attachId == currentAttachId);
    const uniqueAttach = attachs && attachs.length == 1 ? attachs[0] : null;
    const uniqueAttachId = uniqueAttach?.attachId;

    setAnchor(null);
    switch (option) {
      case "edit":
        setDialog(
          <>
            {
              optionMode == "default"
                ?
                <EditPublicationFactory
                  publicationId={publicationId}
                  publicationType={publicationType}
                  masterPublicationId={masterPublication?.publicationId}
                  listExclusiveId={listExclusive?.listId}
                  masterTitle={title}
                  onClose={(success) => { setDialog(null); if (success) onReload(); }}
                />
                :
                publicationFactory(pubId, pubType, (success) => { setDialog(null); if (success) onReload(); }, { question: { questionId: answerDefinition?.questionId } as PublicationInAnswer })
            }
          </>
        );
        break;

      case "delete":
        setDialog(<DeletePublicationFactory
          publicationId={pubId}
          title={title}
          isDraft={isDraft}
          publicationType={publicationType}
          isThread={masterPublication != null}
          onClose={
            (deleted) => {
              if (deleted) onFeedItemChanged({ deletedOn: new Date() });
              setDialog(null);
            }
          }
        />);
        break;

      case "copyLink":
        copyLink();
        break;

      case "qrCode":
        qrcode();
        break;

      case "relatedContent":
        setDialog(<PublicationRelationshipDialog
          publicationId={publicationId}
          title={title}
          onClose={
            (changed) => {
              if (changed) onFeedItemChanged({});
              setDialog(null);
            }
          }
        />);
        break;

      case "history":
        setDialog(<PublicationLogDialog
          publicationId={pubId}
          title={title}
          onClose={closeDialog}
        />);
        break;

      case "customThumbnail":
        setDialog(<PublicationThumbnailDialog
          publicationId={publicationId}
          title={title}
          onClose={
            (changed) => {
              if (changed) onFeedItemChanged({});
              setDialog(null);
            }
          }
        />);
        break;

      case "classify":
        setDialog(classifyFactory(pubId, pubType, title, handleCloseClassify));
        break;

      case "channelPinned":
        handleChannelPinned();
        break;

      case "block":
        setDialog(
          <BlockDialog
            onClose={
              (blocked) => {
                if (blocked) onReload();
                setDialog(null);
              }
            }
            publicationId={pubId}
            title={title}
          />);
        break;

      case "unblock":
        setDialog(
          <MprcConfirmDialog
            open
            severity="question"
            message={t("Feed.Card.Common.UnblockConfirmMessage")}
            onClose={yes => {
              if (yes) setDialog(
                <UnblockPublication
                  publicationId={pubId}
                  onClose={
                    (success) => {
                      if (success) onReload();
                      setDialog(null);
                    }
                  }
                />);
            }}
          />
        );

        break;
      case "abuseOrError":
        setDialog(
          <ReportErrorOrAbuseDialog
            onClose={() => setDialog(null)}
            publicationId={pubId}
            title={title}
          />
        );
        break;

      case "promoteShorts":
        setDialog(
          <EditShortVideoDialog
            shortVideoId={null}
            publicationId={publicationId}
            title={title}
            allowDelete
            onClose={() => setDialog(null)}
          />
        );
        break;

      case "viewTranscription":
        if (currentAttach || uniqueAttachId) {
          setDialog(
            <VideoTranscriptionDrawer
              attachId={currentAttach?.attachId ?? uniqueAttachId!}
              title={title}
              onClose={() => setDialog(null)}
            />
          );
        }
        break;


      case "updatePoster":
        if (currentAttach || uniqueAttachId) {
          setDialog(
            <UpdatePosterDialog
              publicationId={publicationId}
              attachId={currentAttachId ?? uniqueAttachId!}
              title={attachs && attachs.length > 1 ? currentAttach?.fileTitle ?? currentAttach?.fileDescription ?? currentAttach?.fileName ?? title : title}
              onClose={
                (success) => {
                  setDialog(null);
                  if (success) onReload();
                }
              }
            />
          );
        }
        break;

      case "download":
        SessionService.downloadWithToken(
          `/api/v1/KnowledgePublication/DownloadOriginalMedia?attachId=${currentAttachId!}`
          , `${title}.txt`);
        break;

      case "downloadDoc":
        if (uniqueAttach && uniqueAttach.fileId) {
          HtmlUtils.downloadUrl(
            SessionService.current.getAssetUrl(uniqueAttach.fileId)!,
            uniqueAttach.fileName!,
          );
        }
        break;

      case "whoLikes":
        setDialog(
          <WhoLikesDrawer
            publicationId={publicationId}
            title={title}
            onClose={() => setDialog(null)}
          />
        );
        break;

      case "whoView":
        setDialog(
          <WhoViewDrawer
            publicationId={publicationId}
            title={title}
            onClose={() => setDialog(null)}
          />
        );
        break;

      case "hideForYou":
        setDialog(
          <DoHidePublication
            publicationId={pubId}
            isVisible={interaction.isVisible}
            onClose={(success) => {
              if (success) {
                onFeedItemChanged({ interaction: { ...interaction, isVisible: !interaction.isVisible } });
              }
              setDialog(null);
            }}
          />
        );
        break;

      case "createQuestion":
        setDialog(publicationFactory(null, PublicationType.Question, handleClosePublication,
          {
            mentions: [{
              id: publicationId,
              ot: "pub",
              value: title,
              denotationChar: "P"
            }]
          }));
        break;

      case "moderatorMessage":
        setDialog(<ModeratorMessageRejectDialog onClose={() => setDialog(null)} />);
        break;

      case "reorderThreads":
        setDialog(
          <ThreadPublicationsreOrderDialog
            masterPublicationId={masterPublication!.publicationId}
            title={title}
            onClose={
              changed => {
                setDialog(null);
                if (changed) publish("TreadReload", "");
              }
            }
          />
        );
        break;

      case "generateAIQuestion":
        setDialog(<GeneratorQuestionAIPub
          publicationId={pubId}
          onClose={closeDialog}
        />);
        break;
    }
  };

  if (deletedOn != undefined)
    return <TrashedOutOptions />;

  return (
    <>
      {dialog}

      <MprcRequestIndicator type="post" visible={posting} />

      <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

      <MprcFeedTypography variant="action" title={t("shared:Actions.MoreOptions")}>
        <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)}>
          <FontAwesomeIcon icon={faCircleEllipsis} fixedWidth />
        </IconButton>
        <Menu
          anchorEl={anchor}
          open={anchor != null}
          onClose={() => setAnchor(null)}
        >
          {menuItems}
        </Menu>
      </MprcFeedTypography>
    </>
  );
};

export const DefaultOptions: React.FC = () => {
  return <BaseDefaultOptions optionMode="default" />;
};

export const QuestionOptions: React.FC = () => {
  return (
    <BaseDefaultOptions optionMode="question" />
  );
};

export const AnswerOptions: React.FC = () => {
  return (
    <BaseDefaultOptions optionMode="answer" />
  );
};
